import { db } from '@/db/client';
import { addSessionEvent } from '@/db/session-events';
import { compressImage, generateThumbnail } from '@/lib/images/processing';
import { enqueueImagesSync } from '@/lib/realtime-sync/plugins/images';
import type { ImageId, SessionId, UserId } from '@/schemas/ids';
import { parsePlayerId } from '@/schemas/ids';
import type { ImageEntityType, ImageRecord } from '@/schemas/image';
import { createOperationId } from '../../operation-queue';
import { createSystemEvent } from '../../session-helpers';
import type { QueueDependencies } from '../queue-utils';

type AddImageQueueParams = QueueDependencies & {
  sessionId: SessionId | undefined; // Only set if image is part of an active session
  imageId: ImageId;
  entityType: ImageEntityType;
  entityId: string;
  file: File;
  caption?: string;
  isPrimary?: boolean;
  sessionIdForPrivacy?: SessionId; // For session/round images (privacy tracking)
  userId: UserId | undefined;
  timestamp: number;
};

/**
 * Add an image to an entity (player, session, game, team, round)
 * This creates the image record, compresses it, generates thumbnail, and creates the event
 */
export const enqueueAddImage = async ({
  operationQueue,
  set,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  imageId,
  entityType,
  entityId,
  file,
  caption,
  isPrimary = false,
  sessionIdForPrivacy,
  userId,
  timestamp,
}: AddImageQueueParams): Promise<void> => {
  // Compress image and generate thumbnail before enqueuing (async work)
  const compressedBlob = await compressImage(file);
  const thumbnailBlob = await generateThumbnail(file, 'gallery');

  // Determine sessionId for privacy tracking (only for session/round images)
  // sessionIdForPrivacy is only set when uploading images to sessions/rounds
  const privacySessionId: SessionId | undefined =
    entityType === 'session' || entityType === 'round' ? sessionIdForPrivacy : undefined;

  // Create image record
  const imageRecord: ImageRecord = {
    id: imageId,
    entityType,
    entityId,
    localBlob: compressedBlob,
    localThumbnail: thumbnailBlob,
    uploadStatus: 'pending',
    caption,
    uploadedAt: timestamp,
    sessionId: privacySessionId, // Only set for session/round images (privacy tracking)
    userId: userId ?? ('' as UserId),
    createdAt: timestamp,
    updatedAt: timestamp,
    deletedAt: undefined,
  };

  // Save to Dexie before enqueuing
  await db.images.add(imageRecord);

  // If there's no active session, just store the image in Dexie without events
  if (!sessionId) {
    // Trigger image sync to upload pending images to Supabase
    if (userId) {
      enqueueImagesSync(userId);
    }
    return;
  }

  // For images in active sessions, use the event system
  await operationQueue.enqueue<void>({
    id: createOperationId(),
    type: 'addImage',
    dedupKey: `image:add:${sessionId}:${imageId}`,
    priority: 7,
    optimisticUpdate: () => {
      // Update session state optimistically (synchronous)
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }
        if (entityType === 'session') {
          if (!draft.activeSession.imageIds.includes(imageId)) {
            draft.activeSession.imageIds.push(imageId);
          }
        } else if (entityType === 'round') {
          const round = draft.activeSession.rounds.find((r) => r.id === entityId);
          if (round && !round.imageIds.includes(imageId)) {
            round.imageIds.push(imageId);
          }
        } else if (entityType === 'player' && isPrimary) {
          const player = draft.activeSession.players.find((p) => p.id === entityId);
          if (player) {
            player.avatarImageId = imageId;
          }
        }
      });
    },
    rollback: async () => {
      // Remove image record from Dexie
      await db.images.delete(imageId);

      // Refresh session from events
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      // Create ImageAdded event
      await addSessionEvent(
        createSystemEvent(
          sessionId,
          'ImageAdded',
          {
            imageId,
            entityType,
            entityId,
            caption,
            isPrimary,
            uploadedAt: new Date(timestamp).toISOString(),
          },
          userId,
        ),
      );
      await updateUndoRedoAvailability(sessionId);

      // Trigger image sync to upload pending images to Supabase
      if (userId) {
        enqueueImagesSync(userId);
      }
    },
  });
};

type RemoveImageQueueParams = QueueDependencies & {
  sessionId: SessionId | undefined; // Only set if image is part of an active session
  imageId: ImageId;
  userId: UserId | undefined;
  timestamp: number;
};

/**
 * Remove an image (soft delete)
 */
export const enqueueRemoveImage = async ({
  operationQueue,
  set,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  imageId,
  userId,
  timestamp,
}: RemoveImageQueueParams): Promise<void> => {
  // If there's no active session, just soft delete the image in Dexie without events
  if (!sessionId) {
    await db.images.update(imageId, {
      deletedAt: timestamp,
      updatedAt: timestamp,
    });
    return;
  }

  // For images in active sessions, use the event system
  await operationQueue.enqueue<void>({
    id: createOperationId(),
    type: 'removeImage',
    dedupKey: `image:remove:${sessionId}:${imageId}`,
    priority: 7,
    optimisticUpdate: () => {
      set((draft) => {
        // Only update active session if it matches the sessionId
        if (draft.activeSession && draft.activeSession.id === sessionId) {
          // Remove from session images
          draft.activeSession.imageIds = draft.activeSession.imageIds.filter(
            (id) => id !== imageId,
          );
          // Remove from all rounds
          for (const round of draft.activeSession.rounds) {
            round.imageIds = round.imageIds.filter((id) => id !== imageId);
          }
          // Remove from player avatars
          for (const player of draft.activeSession.players) {
            if (player.avatarImageId === imageId) {
              player.avatarImageId = undefined;
            }
          }
        }
      });
    },
    rollback: async () => {
      // Refresh session from events
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      // Soft delete image record
      await db.images.update(imageId, {
        deletedAt: timestamp,
        updatedAt: timestamp,
      });

      // Create ImageRemoved event
      await addSessionEvent(
        createSystemEvent(
          sessionId,
          'ImageRemoved',
          {
            imageId,
          },
          userId,
        ),
      );
      await updateUndoRedoAvailability(sessionId);
    },
  });
};

type UpdateImageCaptionQueueParams = QueueDependencies & {
  sessionId: SessionId | undefined; // Only set if image is part of an active session
  imageId: ImageId;
  caption: string;
  userId: UserId | undefined;
  timestamp: number;
};

/**
 * Update image caption
 */
export const enqueueUpdateImageCaption = async ({
  operationQueue,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  imageId,
  caption,
  userId,
  timestamp,
}: UpdateImageCaptionQueueParams): Promise<void> => {
  // If there's no active session, just update the image in Dexie without events
  if (!sessionId) {
    await db.images.update(imageId, {
      caption,
      updatedAt: timestamp,
    });
    return;
  }

  // For images in active sessions, use the event system
  await operationQueue.enqueue<void>({
    id: createOperationId(),
    type: 'updateImageCaption',
    dedupKey: `image:caption:${sessionId}:${imageId}`,
    priority: 7,
    optimisticUpdate: () => {
      // Caption changes don't affect session state, only image metadata
      // Update is handled in execute
    },
    rollback: async () => {
      // Refresh session from events
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      // Update image record
      await db.images.update(imageId, {
        caption,
        updatedAt: timestamp,
      });

      // Create ImageCaptionChanged event
      await addSessionEvent(
        createSystemEvent(
          sessionId,
          'ImageCaptionChanged',
          {
            imageId,
            caption,
          },
          userId,
        ),
      );
      await updateUndoRedoAvailability(sessionId);
    },
  });
};

type SetPrimaryImageQueueParams = QueueDependencies & {
  sessionId: SessionId | undefined; // Only set if image is part of an active session
  entityType: 'player' | 'game' | 'team' | 'group';
  entityId: string;
  imageId: ImageId;
  userId: UserId | undefined;
};

/**
 * Set an image as primary/avatar for an entity
 */
export const enqueueSetPrimaryImage = async ({
  operationQueue,
  set,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  entityType,
  entityId,
  imageId,
  userId,
}: SetPrimaryImageQueueParams): Promise<void> => {
  // If there's no active session, just update the entity's primary image reference
  // For players, this is stored in the player record directly in Dexie
  // For game/team/group, this is stored in gameDefinitions/teamPresets/playerGroups tables
  if (!sessionId) {
    if (entityType === 'player') {
      // Update player's avatarImageId directly in Dexie
      const { db } = await import('@/db/client');
      await db.players.update(parsePlayerId(entityId), {
        avatarImageId: imageId,
        updatedAt: Date.now(),
      });
    } else if (entityType === 'group') {
      // Update group's avatarImageId directly in Dexie
      const { db } = await import('@/db/client');
      const { parsePlayerGroupId } = await import('@/schemas/ids');
      await db.playerGroups.update(parsePlayerGroupId(entityId), {
        avatarImageId: imageId,
        updatedAt: Date.now(),
      });
    }
    // For game/team, primary image is stored in gameDefinitions/teamPresets tables
    // These are updated via their respective stores, not here
    return;
  }

  // For images in active sessions, use the event system
  await operationQueue.enqueue<void>({
    id: createOperationId(),
    type: 'setPrimaryImage',
    dedupKey: `image:primary:${sessionId}:${entityType}:${entityId}:${imageId}`,
    priority: 7,
    optimisticUpdate: () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }
        if (entityType === 'player') {
          const player = draft.activeSession.players.find((p) => p.id === entityId);
          if (player) {
            player.avatarImageId = imageId;
          }
        }
        // For game/team, primary image is stored in gameDefinitions/teamPresets tables, not session
      });
    },
    rollback: async () => {
      // Refresh session from events
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      // Create ImagePrimarySet event
      await addSessionEvent(
        createSystemEvent(
          sessionId,
          'ImagePrimarySet',
          {
            entityType,
            entityId,
            imageId,
          },
          userId,
        ),
      );
      await updateUndoRedoAvailability(sessionId);
    },
  });
};
